/**
 * Plan Management Drawer
 * 
 * Mobile inline drawer for managing drill plans:
 * - Select a previously created plan
 * - Edit plan name and description
 * - Delete plans
 * - Export plans (JSON, PDF, HTML, CSV)
 * - Import plans (JSON, PDF, HTML, CSV)
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  X, 
  CalendarRange, 
  Loader2, 
  CheckCircle2, 
  ArrowRight, 
  Edit2, 
  Trash2, 
  Download, 
  Upload,
  FileJson,
  FileText,
  FileCode,
  FileSpreadsheet
} from 'lucide-react';
import logger from '@/lib/logging';
import { usePlan } from '@/context/plan-context';
import { useClient } from '@/context/client-context';
import { useToast } from '@/hooks/use-toast';
import { deleteProgram, updateProgram } from '@/components/drill-planner/services/programs-api';
import { Program } from '@/components/drill-planner/types/program-types';
import { formatDistanceToNow } from 'date-fns';
import { 
  downloadProgram, 
  downloadProgramAsPDF, 
  downloadProgramAsHTML, 
  downloadProgramAsCSV,
  uploadProgram,
  uploadProgramFromPDF,
  uploadProgramFromHTML,
  uploadProgramFromCSV
} from '@/utils/programExport';
import { useIsMobile } from '@/hooks/use-mobile';
import Modal from '@/components/shared/Modal';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';

interface PlanManagementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PlanManagementDrawer({ isOpen, onClose }: PlanManagementDrawerProps) {
  const navigate = useNavigate();
  const { client } = useClient();
  const { programs, programsLoading, activePlanId, setActivePlanId, activePlan } = usePlan();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [planToDelete, setPlanToDelete] = useState<Program | null>(null);
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState<string | null>(null);

  const handleSelectPlan = useCallback((planId: string) => {
    setActivePlanId(planId);
    onClose();
  }, [setActivePlanId, onClose]);

  const handleCreatePlan = useCallback(() => {
    onClose();
    navigate('/drill-planner?newPlan=1');
  }, [navigate, onClose]);

  const handleStartEdit = useCallback((program: Program) => {
    setEditingPlanId(program.id);
    setEditName(program.name);
    setEditDescription(program.description || '');
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingPlanId(null);
    setEditName('');
    setEditDescription('');
  }, []);

  const handleSaveEdit = useCallback(async (programId: string) => {
    try {
      const updates: Partial<Program> = {
        name: editName.trim(),
        description: editDescription.trim(),
      };

      await updateProgram(programId, updates);
      toast({
        title: "Success",
        description: "Plan updated successfully!"
      });
      setEditingPlanId(null);
      setEditName('');
      setEditDescription('');
    } catch (error) {
      logger.error('Error updating plan:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update plan",
        variant: "destructive"
      });
    }
  }, [editName, editDescription, toast]);

  const handleDeletePlan = useCallback(async () => {
    if (!planToDelete) return;

    try {
      await deleteProgram(planToDelete.id);
      toast({
        title: "Success",
        description: "Plan deleted successfully!"
      });
      setPlanToDelete(null);
      if (activePlanId === planToDelete.id) {
        setActivePlanId(null);
      }
    } catch (error) {
      logger.error('Error deleting plan:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete plan",
        variant: "destructive"
      });
    }
  }, [planToDelete, toast, activePlanId, setActivePlanId]);

  const handleExport = useCallback(async (program: Program, format: 'json' | 'pdf' | 'html' | 'csv') => {
    setIsExporting(`${program.id}-${format}`);
    try {
      switch (format) {
        case 'json':
          downloadProgram(program);
          break;
        case 'pdf':
          await downloadProgramAsPDF(program);
          break;
        case 'html':
          downloadProgramAsHTML(program);
          break;
        case 'csv':
          downloadProgramAsCSV(program);
          break;
      }
      toast({
        title: "Success",
        description: `Plan exported as ${format.toUpperCase()} successfully!`
      });
    } catch (error) {
      logger.error(`Error exporting plan as ${format}:`, error);
      toast({
        title: "Error",
        description: `Failed to export plan as ${format.toUpperCase()}`,
        variant: "destructive"
      });
    } finally {
      setIsExporting(null);
    }
  }, [toast]);

  const handleImport = useCallback(async (format: 'json' | 'pdf' | 'html' | 'csv') => {
    setIsImporting(format);
    try {
      let importedProgram: Program | null = null;
      
      switch (format) {
        case 'json':
          importedProgram = await uploadProgram();
          break;
        case 'pdf':
          importedProgram = await uploadProgramFromPDF();
          break;
        case 'html':
          importedProgram = await uploadProgramFromHTML();
          break;
        case 'csv':
          importedProgram = await uploadProgramFromCSV();
          break;
      }
      
      if (importedProgram) {
        toast({
          title: "Success",
          description: `Plan imported from ${format.toUpperCase()} successfully!`
        });
        onClose();
      }
    } catch (error) {
      logger.error(`Error importing plan from ${format}:`, error);
      toast({
        title: "Error",
        description: `Failed to import plan from ${format.toUpperCase()}`,
        variant: "destructive"
      });
    } finally {
      setIsImporting(null);
    }
  }, [toast, onClose]);

  if (!isOpen) {
    return null;
  }

  const sortedPrograms = [...programs].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return dateB - dateA;
  });

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="relative mb-4 bg-card border rounded-lg shadow-sm flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold">Manage Plans</h2>
              <p className="text-sm text-muted-foreground">
                {client ? `For ${clientDisplayLabel(client)}` : 'Select a client first'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-4 py-4 pb-6 space-y-4">
              {/* Import Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Import Plan</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImport('json')}
                    disabled={!!isImporting || !client}
                    className="h-10"
                  >
                    {isImporting === 'json' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileJson className="h-4 w-4 mr-2" />
                    )}
                    JSON
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImport('pdf')}
                    disabled={!!isImporting || !client}
                    className="h-10"
                  >
                    {isImporting === 'pdf' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImport('html')}
                    disabled={!!isImporting || !client}
                    className="h-10"
                  >
                    {isImporting === 'html' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCode className="h-4 w-4 mr-2" />
                    )}
                    HTML
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImport('csv')}
                    disabled={!!isImporting || !client}
                    className="h-10"
                  >
                    {isImporting === 'csv' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                    )}
                    CSV
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Plans List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Plan</Label>
                {programsLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading plans...
                  </div>
                ) : sortedPrograms.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-6 text-center space-y-4">
                    <p className="text-sm text-muted-foreground">
                      No intervention plans found for {client?.name ?? 'this client'}.
                    </p>
                    <Button onClick={handleCreatePlan} className="gap-2" disabled={!client}>
                      <CalendarRange className="h-4 w-4" />
                      Create New Plan
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sortedPrograms.map(program => {
                      const updatedAt = program.updatedAt || program.createdAt;
                      const lastUpdated = updatedAt
                        ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
                        : 'Unknown';
                      const isActive = activePlanId === program.id;
                      const isEditing = editingPlanId === program.id;

                      return (
                        <div
                          key={program.id}
                          className={`rounded-lg border p-3 space-y-2 ${
                            isActive ? 'border-primary bg-primary/5' : 'border-border'
                          }`}
                        >
                          {isEditing ? (
                            <div className="space-y-3">
                              <div>
                                <Label htmlFor={`edit-name-${program.id}`} className="text-xs">Plan Name</Label>
                                <Input
                                  id={`edit-name-${program.id}`}
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="h-10 mt-1"
                                  placeholder="Plan name"
                                />
                              </div>
                              <div>
                                <Label htmlFor={`edit-desc-${program.id}`} className="text-xs">Description</Label>
                                <Textarea
                                  id={`edit-desc-${program.id}`}
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                  className="mt-1 min-h-[80px]"
                                  placeholder="Plan description (optional)"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveEdit(program.id)}
                                  className="h-10 flex-1"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                  className="h-10 flex-1"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-sm leading-tight truncate">
                                      {program.name}
                                    </p>
                                    {isActive && (
                                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                                    )}
                                  </div>
                                  {program.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                      {program.description}
                                    </p>
                                  )}
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mt-2">
                                    {program.days.length} day{program.days.length !== 1 ? 's' : ''} • Last updated {lastUpdated}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 pt-2 border-t">
                                <Button
                                  size="sm"
                                  variant={isActive ? "default" : "outline"}
                                  onClick={() => handleSelectPlan(program.id)}
                                  className="h-9 flex-1"
                                >
                                  {isActive ? 'Active' : 'Select'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStartEdit(program)}
                                  className="h-9"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setPlanToDelete(program)}
                                  className="h-9 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                <div className="relative">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {}}
                                    className="h-9"
                                    disabled={!!isExporting}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  {/* Export dropdown would go here - simplified for now */}
                                </div>
                              </div>
                              {/* Export buttons */}
                              <div className="grid grid-cols-4 gap-1 pt-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleExport(program, 'json')}
                                  disabled={isExporting === `${program.id}-json`}
                                  className="h-8 text-xs"
                                  title="Export as JSON"
                                >
                                  {isExporting === `${program.id}-json` ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <FileJson className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleExport(program, 'pdf')}
                                  disabled={isExporting === `${program.id}-pdf`}
                                  className="h-8 text-xs"
                                  title="Export as PDF"
                                >
                                  {isExporting === `${program.id}-pdf` ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <FileText className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleExport(program, 'html')}
                                  disabled={isExporting === `${program.id}-html`}
                                  className="h-8 text-xs"
                                  title="Export as HTML"
                                >
                                  {isExporting === `${program.id}-html` ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <FileCode className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleExport(program, 'csv')}
                                  disabled={isExporting === `${program.id}-csv`}
                                  className="h-8 text-xs"
                                  title="Export as CSV"
                                >
                                  {isExporting === `${program.id}-csv` ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <FileSpreadsheet className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {sortedPrograms.length > 0 && (
                <>
                  <Separator />
                  <Button
                    onClick={handleCreatePlan}
                    className="w-full gap-2"
                    disabled={!client}
                  >
                    <CalendarRange className="h-4 w-4" />
                    Create New Plan
                  </Button>
                </>
              )}
            </div>
          </ScrollArea>
        </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!planToDelete}
        onClose={() => setPlanToDelete(null)}
        title="Delete Plan"
      >
        <p>Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.</p>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => setPlanToDelete(null)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDeletePlan}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

